import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import styles from './CheckboxTree.module.scss'
import Checkbox from '../Checkbox'

export const toTree = (items, id = null, link = 'parent_id') => {
  return items
    .filter((item) => item[link] === id)
    .map((item) => {
      return {
        ...item,
        parentId: item[link],
        children: toTree(items, item.id, link)
      }
    })
}

const flattenNodes = (nodes, dict, parent, depth = 0) => {
  const flatMap = dict ?? new Map()

  nodes.forEach((node) => {
    if (flatMap.has(node.id)) {
      throw Error('Found duplicate entries in tree for node: ', node.id)
    }

    flatMap.set(node.id, {
      id: node.id,
      depth,
      children: node.children,
      parent
    })

    flattenNodes(node.children, flatMap, node, depth + 1)
  })

  return flatMap
}

const TreeContext = createContext({
  checked: [],
  selectNode: undefined
})

const Node = (node) => {
  const nodeRef = useRef()
  const { checked, selectNode } = useContext(TreeContext)

  const isChecked = useMemo(() => checked.includes(node.id), [checked, node.id])
  const isIndeterminate = !isChecked && node.children.some((child) => checked.includes(child.id))

  const handleChange = useCallback(
    (e) => {
      selectNode(node, e.target.checked)
    },
    [selectNode, node]
  )

  return (
    <Checkbox id={node.id} checked={isChecked} isIndeterminate={isIndeterminate} onChange={handleChange} ref={nodeRef}>
      {node.name}
    </Checkbox>
  )
}

const Tree = (props) => {
  const { nodes = [], depth = 0 } = props

  return nodes.map((node) => {
    return (
      <ul key={'Node_' + node.id} className={cx(styles.NodeGroup, { [styles.Nested]: depth > 0 })}>
        <li className={styles.Node}>
          <Node {...node} />
          {node.children.length > 0 && <Tree nodes={node.children} depth={depth + 1} />}
        </li>
      </ul>
    )
  })
}

const toggleChildren = (node, isChecked, checkedSet) => {
  if (node.children.length) {
    node.children.forEach((child) => {
      isChecked ? checkedSet.add(child.id) : checkedSet.delete(child.id)

      if (child.children.length) {
        toggleChildren(child, isChecked, checkedSet)
      }
    })
  }
}

const toggleParent = (node, checkedSet, flatNodes) => {
  if (node.parentId) {
    const parent = flatNodes.get(node.parentId)

    if (!parent || !parent.children.length) {
      return
    }

    const allChecked = parent.children.every((child) => checkedSet.has(child.id))
    allChecked ? checkedSet.add(parent.id) : checkedSet.delete(parent.id)

    toggleParent(parent, checkedSet, flatNodes)
  }
}

const CheckboxTree = (props) => {
  const { onChange, checked, nodes } = props

  const flatNodes = useMemo(() => flattenNodes(nodes), [nodes])

  const selectNode = useCallback(
    (node, isChecked) => {
      const checkedSet = new Set(checked)

      isChecked ? checkedSet.add(node.id) : checkedSet.delete(node.id)

      toggleChildren(node, isChecked, checkedSet)
      toggleParent(node, checkedSet, flatNodes)

      onChange(Array.from(checkedSet))
    },
    [checked, onChange, flatNodes]
  )

  const treeContextValue = {
    checked,
    selectNode
  }

  return (
    <TreeContext.Provider value={treeContextValue}>
      <Tree nodes={nodes} />
    </TreeContext.Provider>
  )
}

CheckboxTree.propTypes = {
  /** id of the selected nodes */
  checked: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /** Change handler */
  onChange: PropTypes.func
}

CheckboxTree.defaultProps = {
  checked: [],
  onChange: undefined
}

export default CheckboxTree
